import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import { createAppSlice, DEFAULT_PACE_SETTINGS } from "./slices/app";
import { createGPSSlice } from "./slices/gps";
import { createGpxSlice } from "./slices/gpx";
import { createLegsSlice } from "./slices/legs";
import { createRecalibrationSlice } from "./slices/recalibration";
import { createSectionsSlice } from "./slices/sections";
import { createStagesSlice } from "./slices/stages";
import { createStatsSlice } from "./slices/stats";
import { createWayPointsSlice } from "./slices/wayPoints";
import { createWeatherSlice } from "./slices/weather";
import { createWorkerSlice } from "./slices/worker";

const useStore = create(
  devtools(
    persist(
      subscribeWithSelector((...a) => ({
        ...createAppSlice(...a),
        ...createGpxSlice(...a),
        ...createStatsSlice(...a),
        ...createWorkerSlice(...a),
        ...createWayPointsSlice(...a),
        ...createLegsSlice(...a),
        ...createSectionsSlice(...a),
        ...createStagesSlice(...a),
        ...createRecalibrationSlice(...a),
        ...createGPSSlice(...a),
        ...createWeatherSlice(...a),
      })),
      {
        name: "terminus-storage",
        merge: (persistedState, currentState) => ({
          ...currentState,
          ...persistedState,
          app: { ...currentState.app, ...(persistedState.app ?? {}) },
          gps: { ...currentState.gps, ...(persistedState.gps ?? {}) },
        }),
        partialize: (state) => ({
          app: {
            trackingMode: state.app.trackingMode,
            displaySlopes: state.app.displaySlopes,
            profileMode: state.app.profileMode,
            pendingUrl: state.app.pendingUrl,
            liveSessionId: state.app.liveSessionId,
            liveWriteKey: state.app.liveWriteKey,
            mode: state.app.mode,
            followerRoomId: state.app.followerRoomId,
            raceId: state.app.raceId,
            currentRoute: state.app.currentRoute,
            theme: state.app.theme,
            outdoorMode: state.app.outdoorMode,
            installPromptDismissed: state.app.installPromptDismissed,
            paceSettings: state.app.paceSettings,
          },
          gps: {
            location: state.gps.location,
            projectedLocation: state.gps.projectedLocation,
            savedLocations: state.gps.savedLocations,
            autoShareEnabled: state.gps.autoShareEnabled,
          },
        }),
        onRehydrateStorage: () => (state) => {
          // Initialize location buffer from persisted locations after rehydration
          if (state?.initLocationBuffer) {
            state.initLocationBuffer();
          }
          // Note: auto-share is resumed by TrailerScreen on mount (not here),
          // where geolocation/worker/raceId are ready and the StrictMode
          // mount/unmount cycle won't tear down the interval we'd create.
          // Re-sync notificationPermission lost by shallow merge during rehydration
          if (
            state?.gps &&
            typeof window !== "undefined" &&
            "Notification" in window
          ) {
            state.gps.notificationPermission =
              Notification.permission === "default"
                ? null
                : Notification.permission;
          }
        },
      },
    ),
    {
      name: "Terminus Store",
      enabled: process.env.NODE_ENV === "development",
      actionsDenylist: ["setCurrentPositionIndex"],
    },
  ),
);

// --- Selector Hooks for Performance ---
// These hooks prevent unnecessary re-renders by selecting only specific slices
export const useAppState = () => useStore((state) => state.app);
export const useGpxData = () => useStore((state) => state.gpx);
export const useStats = () => useStore((state) => state.stats);
export const useWorkerState = () => useStore((state) => state.worker);

// Specific selectors for common use cases
export const useTrackingMode = () =>
  useStore((state) => state.app.trackingMode);
export const useDisplaySlopes = () =>
  useStore((state) => state.app.displaySlopes);
export const useProjectedLocation = () =>
  useStore((state) => state.gps.projectedLocation);
export const useGpxCoordinates = () => useStore((state) => state.gpx.data);
export const useProcessingState = () =>
  useStore(
    useShallow((state) => ({
      isProcessing: state.worker.processing,
      progress: state.worker.progress,
      message: state.worker.progressMessage,
      error: state.worker.errorMessage,
    })),
  );

export { DEFAULT_PACE_SETTINGS };

export default useStore;
